import { List, Map } from 'immutable';
import isNumber from 'lodash/isNumber';
import noop from 'lodash/noop';
import { push, replace } from 'react-router-redux';
import { call, cancel, put, select } from 'redux-saga/effects';

import * as AppActions from '../../../actions';
import * as AppConstants from '../../../constants';
import { getInDomain } from '../../../domains/externalUtils';
import { putAndWait } from '../../../sagas/TaskManager';
import * as AppUtils from '../../../utils';
import * as StartStationHelpers from '../startStation/helpers';

export function* isResuming({
  payload: { id, index, sortOrder, pandoraType, forceRestartSourceIfPlaying },
}) {
  if (forceRestartSourceIfPlaying) {
    return false;
  }

  const childrenSourceTypes = [
    AppConstants.SourceTypes.PandoraTypes.SONG,
    AppConstants.SourceTypes.PandoraTypes.PODCAST_EPISODE,
  ];
  let isPlaying = childrenSourceTypes.includes(pandoraType)
    ? yield select(getInDomain('playback.utils.isTrackPlayingCurrently', false), id)
    : yield select(getInDomain('playback.utils.isSourcePlayingCurrently', false), id);
  if (isPlaying && isNumber(index) && index >= 0) {
    const trackId = yield select(
      getInDomain('playback.utils.getCurrentTrackTokenFromActivePlaylist'),
    );
    const currentTrackIndex = yield select(
      getInDomain('playback.utils.getTrackIndexInOriginalSource'),
      { trackId, sourceId: id, sortOrder },
    );
    isPlaying = currentTrackIndex === index;
  }
  const currentSortOrder = yield select(getInDomain('playback.selectors.getSourceSortOrder', null));
  isPlaying = isPlaying && sortOrder === currentSortOrder;
  return isPlaying;
}

export function* reRoute({ payload: { id, pandoraType, isInitialLoad } }) {
  const adSupported = yield select(AppUtils.Listener.shouldCurrentListenerGetAds);
  if (adSupported && AppUtils.SourceType.isStationType(pandoraType) && !isInitialLoad) {
    const nowPlayingUrl = yield select(
      AppUtils.Route.getNowPlayingPathForId,
      id,
      AppConstants.SourceTypes.SourceTypes.STATION,
    );
    yield put(push(nowPlayingUrl));
  }
}

export function* resumePlayback({ payload: { id, isInitialLoad } }) {
  let intentPlayTrackOptions;
  if (isInitialLoad) {
    if (!AppUtils.Music.isOnDemandIdLike(id)) {
      const stationIds = yield select(getInDomain('playback.utils.getRecencySortedStationIds'));
      if (!stationIds.includes(id)) {
        if (stationIds.size > 0) {
          const shuffleStationId = yield select(getInDomain('playback.utils.getShuffleStationId'));
          const stationId = stationIds.get(0);
          yield put(
            AppActions.UseCases.startStation({
              stationId,
              rerouteBasedOnTier: true,
              initialPlayback: true,
              shouldShuffle: id === shuffleStationId,
            }),
          );
        } else {
          yield put(replace(AppUtils.Route.getCollectionPath()));
        }
        return;
      }
    }
    // Initial load means that we're autoplaying so we don't want to log interaction stats
    intentPlayTrackOptions = {
      logPlaybackActionsAnalytics: false,
      playbackActionsAnalyticsUserInitiated: false,
    };
  }
  yield put(AppActions.Playback.intentPlayTrack(intentPlayTrackOptions));
}

export function* shouldSetInteractiveRadioModeOnPlay({ payload: { isInitialLoad, pandoraType } }) {
  if (!AppUtils.SourceType.isStationType(pandoraType) || !isInitialLoad) {
    return false;
  }

  return yield select(getInDomain('playback.utils.shouldSetInteractiveRadioStartupModeId', false));
}

export function* resumeWithNewMode(action) {
  const modeId = yield select(AppUtils.QueryParams.getInteractiveRadioStartupModeId);
  let mode = yield select(getInDomain('playback.utils.getInteractiveRadioModeById', Map()), modeId);

  if (mode.isEmpty()) {
    yield call(putAndWait, getInDomain('playback.actions.fetchInteractiveRadioModes'));
    mode = yield select(getInDomain('playback.utils.getInteractiveRadioModeById', Map()), modeId);
  }

  if (!mode.isEmpty()) {
    const modes = yield select(getInDomain('playback.utils.getInteractiveRadioModes', List()));
    yield put(
      AppActions.UseCases.changeInteractiveRadioMode({
        modeId,
        stationId: action.payload.id,
        currentModeId: yield select(
          getInDomain('playback.utils.getInteractiveRadioCurrentModeId', null),
        ),
        modeIndex: modes.indexOf(mode),
      }),
    );
  } else {
    // station doesn't have modes
    yield call(resumePlayback, action);
  }
}

export function* shouldUseAPS({ payload }) {
  return yield select(AppUtils.SourceType.isPandoraTypeAPSEnabled, payload.pandoraType);
}

export function* setShuffleMode(action) {
  return yield call(StartStationHelpers.setShuffleMode, action);
}

export function* startStation(action) {
  const { id, pandoraId, shouldShuffle, isInitialLoad, trackToken } = action.payload;
  const params = {
    forceApi: yield call(shouldSetInteractiveRadioModeOnPlay, action),
    initialPlayback: isInitialLoad,
    rerouteBasedOnTier: !isInitialLoad,
    shouldShuffle,
    startingTrackToken: trackToken,
  };
  if (id === pandoraId) {
    params.pandoraId = id;
  } else {
    params.stationId = id;
  }
  yield call(putAndWait, AppActions.UseCases.startStation(params));
}

export function* verifySourceRights({ payload: { context, id, index, sortOrder } }) {
  try {
    yield call(
      putAndWait,
      getInDomain('playback.actions.checkIsPlayableSource')({
        id,
        index,
        sortOrder,
        alertUserIfUnavailable: context !== AppConstants.Ads.Rewards.ContextTypes.DEEP_LINK,
      }),
    );
  } catch (_error) {
    yield cancel();
  }
}

export function* startSource(action) {
  const {
    id,
    index,
    sortOrder,
    shouldShuffle,
    context,
    pandoraId,
    playSourceUsingPandoraId,
    pandoraType,
  } = action.payload;
  let { stationId } = action.payload;
  const isPlaying = yield select(getInDomain('playback.utils.isSourcePlayingCurrently', false), id);
  const isShuffling = yield select(getInDomain('playback.selectors.getSourceShuffleMode', false));
  const repeat = isPlaying
    ? yield select(getInDomain('playback.selectors.getSourceRepeatMode', false))
    : null;

  const shuffle = shouldShuffle || (isPlaying && isShuffling);

  if (playSourceUsingPandoraId) {
    yield call(
      putAndWait,
      getInDomain('playback.actions.playSourceWithItemId')({
        context,
        id,
        index: undefined,
        pandoraId,
        repeat,
        shuffle,
        sortOrder,
      }),
    );
  } else {
    const isStation = AppUtils.SourceType.isStationType(pandoraType);
    if (AppUtils.Listener.isAPSPandoraStationEnabled && shuffle && isStation) {
      const { payload } = yield call(StartStationHelpers.setShuffleMode, action);
      stationId = payload.stationId;
    }

    let sourceId =
      !!stationId || isStation
        ? yield select(getInDomain('playback.utils.getStationPandoraId', pandoraId), stationId)
        : id;
    sourceId = isStation && !sourceId ? pandoraId : sourceId;

    yield call(
      putAndWait,
      getInDomain('playback.actions.playSource')({
        context,
        index,
        pandoraId: sourceId,
        repeat,
        shuffle,
        sortOrder,
      }),
    );
  }
}

export function* startingSource({ payload: { id } }) {
  yield put(AppActions.Integrations.startingSource({ sourceId: id }));
}

export function* sourceStarted({ payload: { id } }) {
  yield put(AppActions.Integrations.sourceStarted({ sourceId: id }));
}

export function* logTTM() {
  yield put(getInDomain('analytics.actions.ttmSourceChangedTransitionFromMusic', noop)());
}
